import { callAI } from '@4cm/core/ai';
import { supabase } from './supabase';

/**
 * Coach-facing session review (spec §04 + migration 00022).
 *
 * One review per session. Coach captures what worked, what struggled,
 * tweaks for the next session. Brain optionally drafts a post-match
 * narrative (spec §08.2) once the notes are in place.
 *
 * Saving is upsert-shaped — a session has at most one review and the
 * coach refines it across visits.
 */

export interface SessionReviewRow {
  id: string;
  session_id: string;
  coach_id: string;
  status: 'draft' | 'complete';
  notes_working: string | null;
  notes_issues: string | null;
  next_session_tweaks: string | null;
  ai_narrative: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SessionReviewInput {
  sessionId: string;
  coachId: string;
  notesWorking: string;
  notesIssues: string;
  nextSessionTweaks: string;
  aiNarrative?: string | null;
  status?: 'draft' | 'complete';
}

type SessionReviewSaveResult =
  | { ok: true; review: SessionReviewRow; source: 'db' | 'local' }
  | { ok: false; error: string };

const LOCAL_REVIEWS_KEY = '4cm_local_session_reviews';

function readLocalReviews(): Record<string, SessionReviewRow> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(LOCAL_REVIEWS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, SessionReviewRow>;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writeLocalReviews(reviews: Record<string, SessionReviewRow>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(LOCAL_REVIEWS_KEY, JSON.stringify(reviews));
  } catch (err) {
    console.warn('[session review] local write failed', err);
  }
}

function saveLocalSessionReview(input: SessionReviewInput): SessionReviewRow {
  const now = new Date().toISOString();
  const status = input.status ?? 'draft';
  const reviews = readLocalReviews();
  const existing = reviews[input.sessionId];
  const review: SessionReviewRow = {
    id: existing?.id ?? `local-review-${input.sessionId}`,
    session_id: input.sessionId,
    coach_id: input.coachId,
    status,
    notes_working: input.notesWorking.trim() || null,
    notes_issues: input.notesIssues.trim() || null,
    next_session_tweaks: input.nextSessionTweaks.trim() || null,
    ai_narrative: input.aiNarrative ?? null,
    completed_at: status === 'complete' ? now : null,
    created_at: existing?.created_at ?? now,
    updated_at: now,
  };
  reviews[input.sessionId] = review;
  writeLocalReviews(reviews);
  return review;
}

export async function loadSessionReview(
  sessionId: string,
): Promise<SessionReviewRow | null> {
  try {
    const { data, error } = await supabase
      .from('session_reviews')
      .select('id, session_id, coach_id, status, notes_working, notes_issues, next_session_tweaks, ai_narrative, completed_at, created_at, updated_at')
      .eq('session_id', sessionId)
      .maybeSingle();
    if (error) {
      console.warn('[session review] load failed', error.message);
    } else if (data) {
      return data as SessionReviewRow;
    }
  } catch (err) {
    console.warn('[session review] load threw', err);
  }
  return readLocalReviews()[sessionId] ?? null;
}

export async function saveSessionReview(
  input: SessionReviewInput,
): Promise<SessionReviewSaveResult> {
  const status = input.status ?? 'draft';
  const completed_at = status === 'complete' ? new Date().toISOString() : null;

  const payload = {
    session_id: input.sessionId,
    coach_id: input.coachId,
    status,
    notes_working: input.notesWorking.trim() || null,
    notes_issues: input.notesIssues.trim() || null,
    next_session_tweaks: input.nextSessionTweaks.trim() || null,
    ai_narrative: input.aiNarrative ?? null,
    completed_at,
  };

  try {
    const { data, error } = await supabase
      .from('session_reviews')
      .upsert(payload, { onConflict: 'session_id' })
      .select('id, session_id, coach_id, status, notes_working, notes_issues, next_session_tweaks, ai_narrative, completed_at, created_at, updated_at')
      .single();
    if (!error && data) return { ok: true, review: data as SessionReviewRow, source: 'db' };
    if (error) console.warn('[session review] save failed; using local fallback', error.message);
  } catch (err) {
    console.warn('[session review] save threw; using local fallback', err);
  }
  return { ok: true, review: saveLocalSessionReview(input), source: 'local' };
}

const NARRATIVE_PROMPT = `You write a short post-session narrative for a youth football coach to share with their team or players.

Audience: the coach, who may forward to parents or share with players.
Tone: development-focused, growth-mindset. Effort and process language, not outcome obsession.
Length: 3 to 4 sentences. ~70 words.
Required: reference at least 2 of the four corners (technical, physical, psychological, social) by name. Identify one strength and one development focus. Suggest a specific next-session theme.
Forbidden: numerical scores, bullet points, headings, exclamation marks, emojis.

Return plain text. No greeting, no signoff, no quotation marks.`;

interface NarrativeOptions {
  sessionTitle: string | null;
  sessionDate: string | null;
  durationMins: number | null;
  objectives: string[];
  notesWorking: string;
  notesIssues: string;
  nextSessionTweaks: string;
  cornerAverages?: { tech?: number; phys?: number; psych?: number; social?: number };
}

const TWEAKS_PROMPT = `You help a youth football coach plan the NEXT session from this one's reflection.

Given the session focus + what worked + what struggled, suggest concrete tweaks for the next session.
Length: 2 to 4 short, specific, actionable lines — one idea per line.
Each line names WHAT to change and WHY in coaching terms (e.g. "Add a defender to the rondo so the press becomes a real decision — possession looked too comfortable").
Forbidden: numerical scores, emojis, headings, numbering, generic filler ("keep up the good work").

Return plain text only — one tweak per line, no greeting, no signoff.`;

interface TweaksOptions {
  sessionTitle: string | null;
  objectives: string[];
  notesWorking: string;
  notesIssues: string;
}

/**
 * Draft concrete "tweaks for the next session" from the coach's reflection.
 * Saved to session_reviews.next_session_tweaks, which the session generator's
 * generationContext reads verbatim on the next generate.
 */
export async function suggestNextSessionTweaks(opts: TweaksOptions): Promise<string | null> {
  const userContent = JSON.stringify({
    session_title: opts.sessionTitle,
    objectives: opts.objectives,
    notes_working: opts.notesWorking,
    notes_issues: opts.notesIssues,
  });
  const text = await callAI({
    system: TWEAKS_PROMPT,
    userContent,
    maxTokens: 220,
    surface: 'brain-chat',
    effort: 'low',
  });
  return text?.trim() ?? null;
}

export async function draftSessionNarrative(opts: NarrativeOptions): Promise<string | null> {
  const userContent = JSON.stringify({
    session_title: opts.sessionTitle,
    session_date:  opts.sessionDate,
    duration_mins: opts.durationMins,
    objectives:    opts.objectives,
    notes_working: opts.notesWorking,
    notes_issues:  opts.notesIssues,
    next_session_tweaks: opts.nextSessionTweaks,
    corner_averages_28d: opts.cornerAverages ?? null,
  });

  const text = await callAI({
    system: NARRATIVE_PROMPT,
    userContent,
    maxTokens: 260,
    surface: 'brain-chat',
    effort: 'low',
  });
  return text?.trim() ?? null;
}
